/**
 * A RECEPÇÃO — a chegada do alevino, e o que se viu dela.
 *
 * ── UMA REGRA DURA, e ela molda a tela inteira ────────────────────────────
 *
 * «SEMPRE, uma recepção sempre abre um ciclo, e para isso o ciclo anterior
 * OBRIGATORIAMENTE precisa estar fechado. Não existe prática de manejo que
 * aceite misturar lotes diferentes de alevinos: isso gera desuniformidade
 * severa.»
 *
 * Por isso esta tela só oferece REPRESA VAZIA. Represa com peixe não aparece
 * na lista — e o servidor recusa de novo, porque a lista do aparelho pode
 * estar velha: alguém povoou o tanque às dez e o tratador registra às onze com
 * a tela que abriu de manhã.
 *
 * ── COMPRADO E RECEBIDO NÃO BATEM, E TUDO BEM ─────────────────────────────
 *
 * O fornecedor manda a mais para cobrir a mortalidade do transporte. Quem
 * entra no ciclo é o RECEBIDO — é esse o peixe que está na água. O comprado
 * fica guardado ao lado porque é número de nota, e é dele que sai a conversa
 * comercial se o lote responder mal.
 *
 * ── O CHECKLIST É TODO FACULTATIVO ────────────────────────────────────────
 *
 * Razão do gestor: «caso eu venha a me reportar com o fornecedor, eu terei
 * dados reais coletados sobre o lote». A resposta do lote vem nas semanas
 * seguintes, e sem o registro da chegada não há com o que comparar.
 *
 * Mas quem recebe alevino tem as mãos ocupadas e o relógio contra — campo
 * obrigatório aqui faria a recepção inteira não ser registrada. Meia
 * conformidade guardada vale muito mais que nenhuma.
 *
 * ── A AMOSTRA É PEQUENA DE PROPÓSITO ──────────────────────────────────────
 *
 * Alevino é sensível: cada manuseio custa. Pesa-se pouco, e o bloco de pesagem
 * aceita isso sem reclamar — a média por peixe aparece igual, e é ela que
 * ancora a estimativa dos próximos meses.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  listarRonda, listarTanques, parametrosDoCalculo, registrarRecepcao,
  type ConformidadeDoLote, type Fase, type Tanque,
} from '../acesso/api'
import { apurar, blocoVazio, BlocosDePesagem, type Bloco } from './BlocosDePesagem'
import { inteiro, numeroNaFaixa, soNumero } from '../dominio/unidades'
import { hojeISO, SeletorDeDia } from './SeletorDeDia'

const RASCUNHO = 'cunhatai.recepcao'

const FASES: readonly { valor: Fase; rotulo: string }[] = [
  { valor: 'alevinagem', rotulo: 'alevinagem' },
  { valor: 'juvenil', rotulo: 'juvenil' },
  { valor: 'engorda', rotulo: 'engorda' },
]

const ESPECIES_PADRAO = ['tambaqui', 'tambatinga', 'pirapitinga', 'pacu', 'outra']

/** O checklist em branco: tudo sem resposta, que é diferente de «não». */
const SEM_RESPOSTA: ConformidadeDoLote = {
  uniformidade: null,
  peixes_ativos: null,
  transporte: null,
  aclimatacao_ok: null,
  alcalinidade_mgl: null,
  ph: null,
  oxigenio_mgl: null,
  vazio_sanitario: null,
}

interface Rascunho {
  tanqueId: string
  dia: string
  fase: Fase
  fornecedor: string
  documento: string
  especie: string
  comprada: string
  recebida: string
  blocos: Bloco[]
  escala: { uniformidade: string; transporte: string }
  sim: { peixes_ativos: string; aclimatacao_ok: string; vazio_sanitario: string }
  agua: { alcalinidade: string; ph: string; oxigenio: string }
  observacao: string
}

function lerRascunho(): Rascunho | null {
  try {
    const bruto = localStorage.getItem(RASCUNHO)
    const r = bruto ? (JSON.parse(bruto) as Rascunho) : null
    return r && Array.isArray(r.blocos) ? r : null
  } catch {
    return null
  }
}

function guardarRascunho(r: Rascunho | null): void {
  try {
    if (r) localStorage.setItem(RASCUNHO, JSON.stringify(r))
    else localStorage.removeItem(RASCUNHO)
  } catch {
    /* armazenamento bloqueado: a ficha vale só enquanto a tela estiver aberta */
  }
}

export function Recepcao({ aoGravar }: { aoGravar: () => void }) {
  const salvo = useMemo(lerRascunho, [])

  const [tanques, setTanques] = useState<Tanque[]>([])
  const [ocupados, setOcupados] = useState<Set<string>>(new Set())
  const [especies, setEspecies] = useState<string[]>(ESPECIES_PADRAO)
  const [tanqueId, setTanqueId] = useState(salvo?.tanqueId ?? '')
  const [dia, setDia] = useState(salvo?.dia ?? hojeISO())
  const [fase, setFase] = useState<Fase>(salvo?.fase ?? 'alevinagem')
  const [fornecedor, setFornecedor] = useState(salvo?.fornecedor ?? '')
  const [documento, setDocumento] = useState(salvo?.documento ?? '')
  const [especie, setEspecie] = useState(salvo?.especie ?? '')
  const [comprada, setComprada] = useState(salvo?.comprada ?? '')
  const [recebida, setRecebida] = useState(salvo?.recebida ?? '')
  const [blocos, setBlocos] = useState<Bloco[]>(salvo?.blocos ?? [blocoVazio()])
  const [escala, setEscala] = useState(salvo?.escala ?? { uniformidade: '', transporte: '' })
  const [sim, setSim] = useState(
    salvo?.sim ?? { peixes_ativos: '', aclimatacao_ok: '', vazio_sanitario: '' },
  )
  const [agua, setAgua] = useState(salvo?.agua ?? { alcalinidade: '', ph: '', oxigenio: '' })
  const [observacao, setObservacao] = useState(salvo?.observacao ?? '')
  const [retomando, setRetomando] = useState(salvo !== null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  // O botão de tentar de novo mexe aqui, e é só isso que ele precisa fazer.
  const [tentativa, setTentativa] = useState(0)

  // A OCUPAÇÃO É DE AGORA, NÃO DO DIA ESCOLHIDO — e por isso `listarRonda()`
  // vai sem o dia.
  //
  // O servidor recusa represa que tenha ciclo aberto HOJE, sem olhar data.
  // Perguntando a ronda do dia escolhido, os dois critérios divergiam: uma
  // recepção lançada para ontem via como livre a represa povoada hoje, a tela
  // aceitava, e a recusa só aparecia no Sincronizar — longe de quem digitou.
  // Se o envio vai ser recusado, a tela tem de recusar antes.
  useEffect(() => {
    Promise.all([listarTanques(), listarRonda(), parametrosDoCalculo()])
      .then(([t, r, p]) => {
        setTanques(t.filter((x) => x.ativo))
        setOcupados(new Set(r.map((x) => x.tanque_id)))
        const lista = (p.parametros.find((x) => x.chave === 'despesca.especies')?.valor ?? '')
          .split(';')
          .map((x) => x.trim())
          .filter((x) => x !== '')
        if (lista.length > 0) setEspecies(lista)
        setEspecie((atual) => atual || lista[0] || ESPECIES_PADRAO[0] || '')
        setErro(null)
      })
      .catch((falha: unknown) => {
        setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
      })
      .finally(() => setCarregando(false))
    // Sem `dia` nas dependências: a ocupação não muda com a data do registro.
    // Com `tentativa`, porque tirar o dia daqui tirou junto o único jeito que
    // havia de refazer o pedido — e quem recebe alevino está no pátio, que é
    // onde o sinal falha. Ficar parado na mensagem de erro até sair da tela e
    // voltar não é resposta para quem acabou de andar até pegar sinal.
  }, [tentativa])

  useEffect(() => {
    const temAlgo = recebida !== '' || fornecedor !== '' || blocos.some((b) => b.bruto !== '')
    guardarRascunho(
      temAlgo
        ? {
            tanqueId, dia, fase, fornecedor, documento, especie, comprada, recebida,
            blocos, escala, sim, agua, observacao,
          }
        : null,
    )
  }, [tanqueId, dia, fase, fornecedor, documento, especie, comprada, recebida,
      blocos, escala, sim, agua, observacao])

  /** Só represa VAZIA. A regra dura do gestor, na lista antes do servidor. */
  const livres = useMemo(
    () => tanques.filter((t) => !ocupados.has(t.id)),
    [tanques, ocupados],
  )

  const amostra = apurar(blocos, true)
  const nRecebida = numeroNaFaixa(recebida, 1, 10_000_000)
  const nComprada = numeroNaFaixa(comprada, 1, 10_000_000)

  if (carregando) return <p className="aviso">Carregando…</p>

  // LISTA VAZIA POR FALTA DE RESPOSTA NÃO É LISTA VAZIA. Sem sinal o app não
  // sabe nada sobre as represas, e dizer «todas têm ciclo aberto» é afirmar um
  // fato que ele não tem como saber — e que levaria alguém a encerrar um ciclo
  // sem precisar. É o mesmo defeito do oxigênio com NaN e da tara ilegível:
  // ausência de dado lida como afirmação.
  //
  // A conferência fica DENTRO do ramo de lista vazia de propósito: `erro`
  // também guarda falha de gravação, e um `return` no topo apagaria a tela
  // inteira quando um registro não entrasse na fila. Só a carga esvazia a
  // lista; depois que ela chega, não volta a esvaziar.
  if (livres.length === 0) {
    return erro !== null ? (
      <>
        <p className="aviso-erro">Não deu para carregar as represas: {erro}</p>
        <button
          type="button"
          className="discreto"
          onClick={() => {
            setCarregando(true)
            setTentativa((n) => n + 1)
          }}
        >
          tentar de novo
        </button>
      </>
    ) : (
      <p className="aviso">
        Todas as represas têm ciclo aberto. Lote novo não entra em cima de lote antigo —
        encerre um ciclo antes de receber.
      </p>
    )
  }

  const tanque = livres.find((t) => t.id === tanqueId) ?? null
  const pronto =
    tanque !== null &&
    nRecebida !== null &&
    Number.isInteger(nRecebida) &&
    amostra !== null

  function gravar() {
    if (!tanque || nRecebida === null || !amostra) return
    setErro(null)
    try {
      const conformidade: ConformidadeDoLote = {
        ...SEM_RESPOSTA,
        uniformidade: (escala.uniformidade || null) as ConformidadeDoLote['uniformidade'],
        transporte: (escala.transporte || null) as ConformidadeDoLote['transporte'],
        peixes_ativos: talvez(sim.peixes_ativos),
        aclimatacao_ok: talvez(sim.aclimatacao_ok),
        vazio_sanitario: talvez(sim.vazio_sanitario),
        alcalinidade_mgl: numeroNaFaixa(agua.alcalinidade, 0, 1000),
        ph: numeroNaFaixa(agua.ph, 0.1, 14),
        oxigenio_mgl: numeroNaFaixa(agua.oxigenio, 0, 30),
      }

      registrarRecepcao(
        {
          tanque_id: tanque.id,
          data: dia,
          fase,
          fornecedor: fornecedor.trim() === '' ? null : fornecedor.trim(),
          documento: documento.trim() === '' ? null : documento.trim(),
          especie: especie.trim() === '' ? null : especie.trim(),
          quantidade_comprada: nComprada,
          quantidade_recebida: nRecebida,
          peso_medio_g: amostra.mediaG,
          observacao: observacao.trim() === '' ? null : observacao.trim(),
          conformidade,
        },
        { rotulo: tanque.codigo },
      )

      setFornecedor('')
      setDocumento('')
      setComprada('')
      setRecebida('')
      setBlocos([blocoVazio()])
      setEscala({ uniformidade: '', transporte: '' })
      setSim({ peixes_ativos: '', aclimatacao_ok: '', vazio_sanitario: '' })
      setAgua({ alcalinidade: '', ph: '', oxigenio: '' })
      setObservacao('')
      guardarRascunho(null)
      setRetomando(false)
      aoGravar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  return (
    <section className="bloco">
      <h2>Recepção de alevinos</h2>

      {retomando && (
        <p className="secundario">Retomando uma recepção que ficou aberta neste aparelho.</p>
      )}

      <label className="campo">
        <span className="campo-rotulo">Em qual represa?</span>
        <select value={tanqueId} onChange={(e) => setTanqueId(e.target.value)}>
          <option value="">escolha…</option>
          {livres.map((t) => (
            <option key={t.id} value={t.id}>{t.codigo} · {t.nome}</option>
          ))}
        </select>
        <span className="campo-dica">
          Só aparecem represas vazias. Lote novo não entra em cima de lote antigo.
        </span>
      </label>

      <SeletorDeDia dia={dia} aoMudar={setDia} />

      <h3>O lote</h3>

      <div className="pesagem-campos">
        <label className="campo">
          <span className="campo-rotulo">Fornecedor</span>
          <input
            value={fornecedor}
            onChange={(e) => setFornecedor(e.target.value)}
            placeholder="Alevinos do Jamari"
          />
        </label>
        <label className="campo">
          <span className="campo-rotulo">Nota / guia</span>
          <input
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
            placeholder="NF 4521"
          />
        </label>
      </div>

      <label className="campo">
        <span className="campo-rotulo">Espécie</span>
        <select value={especie} onChange={(e) => setEspecie(e.target.value)}>
          {especies.map((x) => (
            <option key={x} value={x}>{x}</option>
          ))}
        </select>
      </label>

      <div className="pesagem-campos">
        <label className="campo">
          <span className="campo-rotulo">Comprados</span>
          <input
            value={comprada}
            onChange={(e) => setComprada(soNumero(e.target.value))}
            inputMode="numeric"
            placeholder="5.000"
          />
        </label>
        <label className="campo">
          <span className="campo-rotulo">Recebidos</span>
          <input
            value={recebida}
            onChange={(e) => setRecebida(soNumero(e.target.value))}
            inputMode="numeric"
            placeholder="5.150"
          />
        </label>
      </div>

      {nComprada !== null && nRecebida !== null && nRecebida !== nComprada && (
        <p className="secundario">
          {nRecebida > nComprada
            ? `Chegaram ${inteiro(nRecebida - nComprada)} a mais que o comprado.`
            : `Chegaram ${inteiro(nComprada - nRecebida)} a menos que o comprado.`}{' '}
          Entram no ciclo os {inteiro(nRecebida)} recebidos.
        </p>
      )}

      <label className="campo">
        <span className="campo-rotulo">Fase do ciclo que abre aqui</span>
        <select value={fase} onChange={(e) => setFase(e.target.value as Fase)}>
          {FASES.map((f) => (
            <option key={f.valor} value={f.valor}>{f.rotulo}</option>
          ))}
        </select>
      </label>

      <h3>A amostra</h3>
      <p className="secundario">
        Poucos peixes: alevino é sensível e cada manuseio custa.
      </p>
      <BlocosDePesagem blocos={blocos} aoMudar={setBlocos} rotulo="Amostra" />

      {/* ---- O CHECKLIST, todo facultativo -------------------------------- */}
      <h3>Como o lote chegou</h3>
      <p className="secundario">
        Tudo opcional. Serve para ter dado real se for preciso conversar com o fornecedor
        daqui a algumas semanas.
      </p>

      <Escala
        rotulo="Uniformidade do lote"
        valor={escala.uniformidade}
        aoMudar={(v) => setEscala((a) => ({ ...a, uniformidade: v }))}
      />
      <Escala
        rotulo="Qualidade do transporte"
        valor={escala.transporte}
        aoMudar={(v) => setEscala((a) => ({ ...a, transporte: v }))}
      />
      <SimNao
        rotulo="Peixes ativos"
        valor={sim.peixes_ativos}
        aoMudar={(v) => setSim((a) => ({ ...a, peixes_ativos: v }))}
      />
      <SimNao
        rotulo="Aclimatação bem executada"
        valor={sim.aclimatacao_ok}
        aoMudar={(v) => setSim((a) => ({ ...a, aclimatacao_ok: v }))}
      />
      <SimNao
        rotulo="Vazio sanitário na represa"
        valor={sim.vazio_sanitario}
        aoMudar={(v) => setSim((a) => ({ ...a, vazio_sanitario: v }))}
      />

      <div className="pesagem-campos">
        <label className="campo">
          <span className="campo-rotulo">Alcalinidade</span>
          <input
            value={agua.alcalinidade}
            onChange={(e) => setAgua((a) => ({ ...a, alcalinidade: soNumero(e.target.value) }))}
            inputMode="decimal"
            placeholder="25"
          />
        </label>
        <label className="campo">
          <span className="campo-rotulo">pH</span>
          <input
            value={agua.ph}
            onChange={(e) => setAgua((a) => ({ ...a, ph: soNumero(e.target.value) }))}
            inputMode="decimal"
            placeholder="6,8"
          />
        </label>
        <label className="campo">
          <span className="campo-rotulo">Oxigênio</span>
          <input
            value={agua.oxigenio}
            onChange={(e) => setAgua((a) => ({ ...a, oxigenio: soNumero(e.target.value) }))}
            inputMode="decimal"
            placeholder="4,2"
          />
        </label>
      </div>

      <label className="campo">
        <span className="campo-rotulo">Observação (opcional)</span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="chegou às 6h, água do saco a 28 °C"
        />
      </label>

      {erro && <p className="aviso-erro">{erro}</p>}

      <p className="aviso-forte">
        Isto <strong>abre um ciclo</strong> na represa escolhida, com o código gerado pelo app.
      </p>

      <button type="button" className="confirma" disabled={!pronto} onClick={gravar}>
        ✓ registrar recepção
      </button>
    </section>
  )
}

/** bom · médio · ruim — e a quarta opção é não responder, clicando de novo. */
function Escala({
  rotulo, valor, aoMudar,
}: {
  rotulo: string
  valor: string
  aoMudar: (v: string) => void
}) {
  const OPCOES = [
    { v: 'bom', r: 'bom' },
    { v: 'medio', r: 'médio' },
    { v: 'ruim', r: 'ruim' },
  ]
  return (
    <div className="campo">
      <span className="campo-rotulo">{rotulo}</span>
      <div className="apuracao compacta">
        {OPCOES.map((o) => (
          <button
            key={o.v}
            type="button"
            className={valor === o.v ? 'unidade-botao ativo' : 'unidade-botao'}
            // Clicar no que já está marcado desmarca: sem isso, quem tocou sem
            // querer fica obrigado a responder o que não observou.
            onClick={() => aoMudar(valor === o.v ? '' : o.v)}
          >
            {o.r}
          </button>
        ))}
      </div>
    </div>
  )
}

function SimNao({
  rotulo, valor, aoMudar,
}: {
  rotulo: string
  valor: string
  aoMudar: (v: string) => void
}) {
  return (
    <div className="campo">
      <span className="campo-rotulo">{rotulo}</span>
      <div className="apuracao compacta">
        {[{ v: 'sim', r: 'sim' }, { v: 'nao', r: 'não' }].map((o) => (
          <button
            key={o.v}
            type="button"
            className={valor === o.v ? 'unidade-botao ativo' : 'unidade-botao'}
            onClick={() => aoMudar(valor === o.v ? '' : o.v)}
          >
            {o.r}
          </button>
        ))}
      </div>
    </div>
  )
}

/** «sim» e «não» viram booleano; em branco continua NULO — nunca falso. */
function talvez(v: string): boolean | null {
  return v === 'sim' ? true : v === 'nao' ? false : null
}
